<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import SearchHeader from '../../SearchHeader/index.vue'
import { useSidebarStore } from '@/stores/sidebar'
import IconButton from '@/components/IconButton/index.vue'
import Button from '~~/src/components/ui/button/Button.vue'
import LibraryFilterMenu from '~~/src/components/Library/TableLibrary/LibraryFilters/LibraryFilterMenu.vue'
import { useLibraryTableFilter } from '~~/src/components/Library/TableLibrary/composables/useTableFilter'

defineOptions({ name: 'DrawerHeader' })

const { state, toggleSidebar } = useSidebarStore()
const router = useRouter()
const { isSmaller } = useBreakpoints(breakpointsTailwind)
const isMobile = ref(isSmaller('md'))
useResizeObserver(window.document.body, () => {
  isMobile.value = isSmaller('md')
})

const searchHeaderRef = ref<InstanceType<typeof SearchHeader> | null>(null)

const {
  separatedByType,
  filterByMe,
  savedMode,
  setSeparatedByType,
  setFilterByMe,
  setSavedMode,
} = useLibraryTableFilter({
  separatedByType: true,
  filterByMe: false,
  savedMode: false,
})

const toggleSeparatedByType = (value: boolean) => {
  setSeparatedByType(value)
}

const toggleFilterByMe = (value: boolean) => {
  setFilterByMe(value)
}

const toggleSavedMode = (value: boolean) => {
  setSavedMode(value)
}

useEventListener('keydown', (event) => {
  if ((event.key === 'f' && event.ctrlKey) || (event.key === 'f' && event.metaKey)) {
    if (state.isOpen) {
      searchHeaderRef.value?.focusField()
      event.preventDefault()
    }
  }
})

const expandLibrary = async () => {
  router.push({
    path: '/library',
  })
}
</script>

<template>
  <div class="flex justify-center items-center z-36 bg-white min-h-[58px] items-center w-full pr-3 md:pr-[50px] relative">
    <TransitionSoftSlide direction="down">
      <div v-if="state.mode === 'view' && state.isOpen" class="flex flex-col gap-2 w-full">
        <div class="flex gap-2 items-center w-full">
          <SearchHeader class="ml-3 w-full" />

          <LibraryFilterMenu
            :separated-by-type="separatedByType"
            :filter-by-me="filterByMe"
            :saved-mode="savedMode"
            :just-icon="true"
            @update:separated-by-type="toggleSeparatedByType"
            @update:filter-by-me="toggleFilterByMe"
            @update:saved-mode="toggleSavedMode"
          />

          <Button variant="ghost" size="icon" class="shrink-0 hidden text-darkGray md:flex" @click="expandLibrary">
            <IconArrowExpand class="w-6 h-6" />
          </Button>
        </div>
      </div>
    </TransitionSoftSlide>

    <div class="h-full">
      <TransitionSoftSlide direction="up">
        <div
          v-if="state.mode !== 'view'"
          id="drawer-header-content"
          class="absolute top-0 left-0 w-full h-[58px] max-w-full overflow-hidden flex opacity-0 transition-opacity duration-300  pr-[50px]"
          :class="{
            '!opacity-100': state.isOpen,
          }"
        />
      </TransitionSoftSlide>
    </div>

    <IconButton v-if="!isMobile" class="w-9 h-9 absolute right-[12px] top-[10px] z-99" title="Toggle Your Library" rotate-180 rounded @click="toggleSidebar()">
      <template v-if="state.isOpen">
        <IconDockLeft class="w-6 h-6 border-box" />
      </template>
      <template v-else>
        <IconDockRight class="w-6 h-6" />
      </template>
    </IconButton>
  </div>
  <div v-if="!state.isOpen" p-2 flex justify-center items-center>
    <div invisible sm:visible text-xs border-1 border-softGray p-1 rounded text-darkGray>
      <span>Alt</span>
      <span>/</span>
    </div>
  </div>
</template>

<style module lang="scss">
 .no-scrollbar{
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  &::-webkit-scrollbar {
    display: none;
    width: 0px;
    background: transparent;
  }
 }
</style>
